import { IImageLibraryDto } from "@kuika/kuika-cl-model";
import classNames from "classnames";
import { Component } from "react";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { FilePicker } from "../../../../common/file-picker";
import { ResourceImage } from "../../../../common/resource-image";
import KuikaPopover from "../../../../ui-kit/kuika-popover/kuika-popover";
import "./app-settings-config-favicon-input.scss";

export interface IImage {
  iconBase64: string;
  icon: string;
}

export interface IFavIconFieldProps {
  /** Antd-injected: the persisted value at this field's form path. */
  value?: string | null;
  /** Antd-injected: writes the form path this field is bound to. */
  onChange?: (value: string | null) => void;
  allImage?: IImageLibraryDto[];
  acceptTpye?: string;
  isRounded?: boolean;
  handleGetImageLibrary: () => void;
}

/**
 * `FavIconInput` bound to a `Form.Item`.
 *
 * `FavIconInput` predates the form owning this value: its props are `icon`/`handleChangeImage`,
 * not the `value`/`onChange` pair `Form.Item` injects into its child, and choosing an image
 * hands back the whole `{ icon, iconBase64 }` pair rather than the bare path the form stores.
 * This is the thin translation between the two, so the six places this picker is used — Web,
 * iOS, Android's square and round icons, Electron, Run Settings — all bind it the same way:
 * `<Form.Item name={[slice, key]}><FavIconField .../></Form.Item>`.
 */
export const FavIconField = ({
  value,
  onChange,
  allImage,
  acceptTpye,
  isRounded,
  handleGetImageLibrary
}: IFavIconFieldProps) => (
  <FavIconInput
    icon={value ?? null}
    allImage={allImage}
    acceptTpye={acceptTpye}
    isRounded={isRounded}
    handleGetImageLibrary={handleGetImageLibrary}
    handleChangeImage={(image: IImage | null): void => onChange?.(image?.icon ?? null)}
  />
);

export interface IFavIconInputProps {
  allImage?: IImageLibraryDto[];
  acceptTpye?: string;
  isRounded?: boolean;
  handleGetImageLibrary: () => void;
  handleChangeImage: (image: IImage | null) => void;
  icon: string | null;
}
export interface IFavIconInputState {
  isIconPopoverVisible: boolean;
}

export class FavIconInput extends Component<IFavIconInputProps, IFavIconInputState> {
  constructor(props) {
    super(props);
    this.state = {
      isIconPopoverVisible: false
    };
  }

  // The library is fetched when the picker opens. It used to hang off a click handler
  // wrapping the popover, and the picker — portaled by Radix but still a React child —
  // bubbled every click inside it back out to that handler, refetching on each one.
  handlePopoverVisibleChange = (visible: boolean): void => {
    this.setState({ isIconPopoverVisible: visible });
    if (visible) this.props.handleGetImageLibrary();
  };

  render() {
    const image = this.props.allImage?.find((icon) => icon.fileName === this.props.icon);
    const isMainAi = KMainFunctions.isMainAI();

    return (
      <>
        <div className="kuika-config-favicon">
          <div className="kuika-config-favicon__anchor">
            <KuikaPopover
              position="left"
              className="kuika-file-picker-popover"
              hideCloseIcon
              // The picker panel is wide and these live in the pane's narrow right column,
              // so without a collision gap Radix flushes it against the viewport edge.
              collisionPadding={16}
              content={
                <FilePicker
                  files={this.props.allImage || []}
                  acceptedFileTypes={this.props.acceptTpye || ".png,.jpg,.svg"}
                  getImageLibrary={this.props.handleGetImageLibrary}
                  onChange={(_value, image) => {
                    this.props.handleChangeImage({
                      icon: image.fileName,
                      iconBase64: image.content
                    });
                  }}
                  onDeselect={() => {
                    this.props.handleChangeImage(null);
                  }}
                  enableDeselect
                  showFileDragger
                  selectedId={image?.fileName}
                />
              }
              visible={this.state.isIconPopoverVisible}
              onVisibleChange={this.handlePopoverVisibleChange}
            >
              <button type="button" className="kuika-config-favicon__trigger">
                <div
                  className={classNames("kuika-config-favicon__thumb", {
                    "kuika-config-favicon__thumb--rounded": this.props.isRounded
                  })}
                >
                  {!image?.content ? (
                    isMainAi ? (
                      <img className="kuika-config-favicon__art" src="/assets/main-ai/config-manager-icon.svg" />
                    ) : (
                      <svg
                        className="kuika-config-favicon__art"
                        width="48"
                        height="48"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M20.3945 0C22.4192 0 24 1.62598 24 3.5976V20.4024C24 22.4232 22.4192 24 20.3945 24H3.60438C1.58023 24 0 22.374 0 20.4024V3.5976C0 1.57684 1.6288 0 3.60438 0H20.3945Z"
                          fill="#246EFF"
                        />
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M9.37536 4.89031C9.90478 4.60257 10.4027 4.45703 10.8552 4.45703C11.19 4.45703 11.443 4.53608 11.6242 4.61112L11.7818 4.68176L11.9672 4.77384C12.432 5.02519 12.7478 5.36832 12.9081 5.79591L12.9599 5.92826C13.024 6.10847 13.0935 6.42493 12.9109 6.85988C12.7672 7.20252 12.4117 7.5799 12.1254 7.85638L11.7906 8.17194L11.6083 8.35433C10.617 9.35649 9.46254 10.5855 9.48102 12.003C9.4643 13.2801 10.4077 14.4076 11.3218 15.353L11.7486 15.7871L12.1256 16.1436C12.4117 16.4202 12.7672 16.7973 12.9109 17.1399C13.0935 17.5748 13.024 17.8913 12.9599 18.0715L12.9081 18.2044L12.8491 18.3435C12.6546 18.7514 12.3047 19.0749 11.8078 19.3063L11.6239 19.3889C11.4427 19.4637 11.1899 19.5427 10.8546 19.5427C10.4027 19.5427 9.90422 19.3972 9.37536 19.11C6.93239 17.784 5.1151 15.7676 4.22886 14.3233C3.73927 13.5236 3.4516 12.9197 3.4339 12.1976L3.42871 12.0688L3.42943 11.9115L3.44537 11.6085C3.50557 10.9691 3.78378 10.4035 4.22886 9.67706C5.1151 8.23222 6.93183 6.21637 9.37536 4.89031ZM11.6565 9.58747C12.1891 8.87345 14.0778 6.51418 15.771 6.51418C15.8276 6.51418 15.8819 6.51649 15.9346 6.52344C17.6847 6.75334 19.3426 8.59487 20.0309 9.77681C21.1481 11.6966 20.2108 13.344 20.0527 13.5971L19.9375 13.7566C19.4895 14.3481 17.5405 16.7816 15.7919 16.7995L15.6893 16.7973L15.6216 16.7885C13.7542 16.393 12.1065 14.6493 11.5105 13.6249C10.3939 11.7057 11.3312 10.0716 11.4897 9.82057L11.6565 9.58747Z"
                          fill="white"
                        />
                      </svg>
                    )
                  ) : (
                    <ResourceImage className="kuika-config-favicon__art" src={image.content} alt="" />
                  )}
                </div>
                <div className="kuika-config-favicon__meta">
                  <span
                    className="kuika-config-favicon__name"
                    title={this.props.icon || (isMainAi ? "Main-AI-logo.jpg" : "Kuika-logo.jpg")}
                  >
                    {this.props.icon || (isMainAi ? "Main-AI-logo.jpg" : "Kuika-logo.jpg")}
                  </span>
                  <span data-testid="appIconsChooseButton" className="kuika-config-favicon__choose">
                    Choose
                  </span>
                </div>
              </button>
            </KuikaPopover>
          </div>
        </div>
      </>
    );
  }
}
